import { useState } from "react";
import { createContainer } from "unstated-next";
import { MygruBackendService } from "../services/MygruBackendService";
import { PointCardService } from "../services/PointCardService";
import { ShopService } from "../services/ShopService";
import { Shop } from "./Shops";

type PointCard = {
  userId: string;
  charge: number;
  isEntry: boolean;
  recommends?: Shop[];
  isLatest: boolean;
  updateResult: string;
};

/**
 * ポイントカードコンテナ
 */
const usePointCardContainer = () => {
  const [pointCard, setPointCard] = useState<PointCard | null>(null);

  /**
   * ポイントカードを初期化
   * @param userId ユーザーID
   * @param campaignId キャンペーンID
   * @param defaultRecommends デフォルトのおすすめ店舗 ID リスト
   */
  const initPointCard = async (
    userId: string,
    hashedUserId: string,
    campaignId: string,
    defaultRecommends: string[],
    apiToken: string
  ) => {

    let updateResult = "OK";
    let isLatest = false;
    let pointCardData = await PointCardService().getPointCard(hashedUserId, campaignId);
    if (!pointCardData.data?.isEntry) {
      // 未エントリーの場合場合、初期化APIを呼び出しデータを再取得
      await MygruBackendService(apiToken).initPointCard(userId, campaignId);
      pointCardData = await PointCardService().getPointCard(hashedUserId, campaignId);
    } else {
      // エントリー済みの場合、ポイントカードを更新
      updateResult = await MygruBackendService(apiToken).updatePointCard(userId, campaignId);
      pointCardData = await PointCardService().getPointCard(hashedUserId, campaignId);
      isLatest = true;
    }

    if (!pointCardData.data) {
      throw new Error(
        `Failed initPointCard. userId: ${userId} campaignId: ${campaignId}`
      );
    }

    // おすすめ店舗データを取得（並列非同期）
    let recommends: Shop[] | undefined = undefined;
    if (pointCardData.data.recommends) {
      // 個別におすすめ店舗が登録されている場合 => 登録済みの店舗を採用
      recommends = await Promise.all<Shop>(
        pointCardData.data.recommends.map(async (s: any): Promise<Shop> => {
          const shopData = await ShopService().getShop(campaignId, s.id);
          return {
            id: shopData.id,
            name: shopData.data.displayName,
            image: shopData.data.image,
            floor: shopData.data.floor,
            basicInfo: shopData.data.basicInfo,
          };
        })
      );
    } else {
      // 個別におすすめ店舗が登録されていない場合 => デフォルトの店舗を採用
      recommends = await Promise.all<Shop>(
        defaultRecommends.map(async (s: string): Promise<Shop> => {
          const shopData = await ShopService().getShop(campaignId, s);
          return {
            id: shopData.id,
            name: shopData.data.displayName,
            image: shopData.data.image,
            floor: shopData.data.floor,
            basicInfo: shopData.data.basicInfo,
          };
        })
      );
    }

    const pointCard = {
      userId: pointCardData.data.userId,
      charge: pointCardData.data.charge,
      isEntry: pointCardData.data.isEntry,
      recommends: recommends,
      isLatest: isLatest,
      updateResult: updateResult
    };

    setPointCard({ ...pointCard });
  };

  /**
   * ポイントカードのエントリー状態を更新する
   * @param campaignId キャンペーンID
   * @param campaignFrom キャンペーン開始日
   * @param campaignTo キャンペーン終了日
   * @returns 更新結果 ok: 成功 | preparing: キャンペーン開始前 | end: キャンペーン終了後
   */
  const entry = async (
    campaignId: string,
    campaignFrom: Date,
    campaignTo: Date
  ) => {
    if (!pointCard) {
      return;
    }

    // エントリー状態を更新
    const result = await PointCardService().entry(
      campaignId,
      pointCard.userId,
      campaignFrom,
      campaignTo
    );
    if (result === "ok") {
      pointCard.isEntry = true;
      setPointCard({ ...pointCard });
    }

    return result;
  };

  return { pointCard, initPointCard, entry };
};

export const PointCardContainer = createContainer(usePointCardContainer);
